/**
 * useProfessionalsListTour
 * Tour for the professionals list page
 */

import { Tour } from '@shared/lib/tours/types'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useEffect } from 'react'
import { usePageReadyForTour } from './usePageReadyForTour'
import { markTourAsCompleted, useTourManager } from './useTourManager'

export function useProfessionalsListTour(autoStart: boolean = false): Tour {
  const { isTourCompleted, shouldShowTour, resetTour } = useTourManager()
  const isPageReady = usePageReadyForTour()
  const TOUR_NAME = 'professionals-list'

  const startTour = () => {
    const professionalsTour = driver({
      showProgress: true,
      nextBtnText: 'Siguiente',
      prevBtnText: 'Anterior',
      doneBtnText: '¡Listo!',
      progressText: '{{current}} de {{total}}',
      smoothScroll: false,

      onDestroyed: () => {
        markTourAsCompleted(TOUR_NAME)
      },

      steps: [
        {
          element: '[data-tour="professionals-grid"]',
          popover: {
            title: 'Listado de Profesionales',
            description:
              'Aquí encontrarás todos los profesionales de esta especialidad.',
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '[data-tour="professionals-filters"]',
          popover: {
            title: 'Filtra y Ordena',
            description:
              'Usá estos controles para filtrar profesionales por calificación, ubicación, y ordenar los resultados según tus preferencias.',
            side: 'bottom',
          },
        },
        {
          element: '[data-tour="professional-card-0"]',
          popover: {
            title: 'Tarjetas de Profesionales',
            description:
              'Cada tarjeta muestra información clave del profesional: nombre, especialidad, calificación y ubicación. Hacé clic para ver el perfil completo.',
            side: 'top',
          },
        },
      ],
    })

    professionalsTour.drive()
  }

  // Auto-start tour on mount if conditions are met
  useEffect(() => {
    // Only start tour when page is ready (loaded and no PWA prompt)
    if (!isPageReady) return

    if (autoStart && shouldShowTour(TOUR_NAME)) {
      // Small delay to ensure DOM elements are rendered
      const timer = setTimeout(() => {
        startTour()
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [autoStart, isPageReady])  

  const reset = () => {
    resetTour(TOUR_NAME)
  }

  return {
    startTour,
    isCompleted: isTourCompleted(TOUR_NAME),
    reset,
  }
}
